import { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import { ChevronLeft, ChevronRight, ExternalLink, Play, X } from 'lucide-react'
import { Section } from '@/components/layout/Section'
import { Container } from '@/components/layout/Container'
import { AnimateIn } from '@/components/motion/AnimateIn'
import {
  formatMediaCount,
  getShowcaseOpenUrl,
  getShowcaseThumbnailFallbacks,
  getYoutubeEmbedUrl,
  getYoutubePreviewEmbedUrl,
} from '@/lib/videoUtils'
import {
  SLIDER_EDGE_INNER,
  SLIDER_NAV_ICON_STROKE,
  sliderNavIconClass,
  sliderNavNextClass,
  sliderNavPrevClass,
} from '@/lib/sliderNavStyles'
import type { ShowcaseVideo } from '@/types/home'
import { cn } from '@/lib/utils'

interface VideoShowcaseProps {
  videos: ShowcaseVideo[]
}

export function VideoShowcase({ videos }: VideoShowcaseProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [modalVideo, setModalVideo] = useState<ShowcaseVideo | null>(null)

  const autoplayPlugin = useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true }),
  )

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: videos.length > 1, align: 'start', containScroll: 'trimSnaps' },
    videos.length > 1 ? [autoplayPlugin.current] : [],
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', () => {
      setScrollSnaps(emblaApi.scrollSnapList())
      onSelect()
    })
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    if (!modalVideo) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setModalVideo(null)
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    }
  }, [modalVideo])

  const openVideo = (video: ShowcaseVideo) => {
    if (video.platform === 'instagram') {
      window.open(getShowcaseOpenUrl(video), '_blank', 'noopener,noreferrer')
      return
    }
    setModalVideo(video)
  }

  if (videos.length === 0) return null

  const navIcon = sliderNavIconClass('md')

  return (
    <div className="bg-page">
      <Container>
        <AnimateIn>
          <Section
            title="Customer Reviews Videos"
            subtitle="Hear real experiences from our happy customers."
          >
            <div className="overflow-visible pl-0 pr-0 md:pr-[22px]">
              <div className={SLIDER_EDGE_INNER}>
                <div className="overflow-hidden" ref={emblaRef}>
                  <div className="-ml-3 flex touch-pan-y sm:-ml-4">
                    {videos.map((video, index) => (
                      <div
                        key={video.id}
                        className="min-w-0 flex-[0_0_88%] pl-3 sm:flex-[0_0_50%] sm:pl-4 md:flex-[0_0_33.333%]"
                      >
                        <VideoCard
                          video={video}
                          isActive={index === selectedIndex}
                          onOpen={() => openVideo(video)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {videos.length > 1 && (
                  <>
                    <button
                      type="button"
                      aria-label="Previous video"
                      disabled={!canScrollPrev}
                      onClick={() => emblaApi?.scrollPrev()}
                      className={cn(sliderNavPrevClass('md', 'image'), 'hidden md:flex')}
                    >
                      <ChevronLeft className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
                    </button>
                    <button
                      type="button"
                      aria-label="Next video"
                      disabled={!canScrollNext}
                      onClick={() => emblaApi?.scrollNext()}
                      className={cn(sliderNavNextClass('md', 'image'), 'hidden md:flex')}
                    >
                      <ChevronRight className={navIcon} strokeWidth={SLIDER_NAV_ICON_STROKE} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {scrollSnaps.length > 1 && (
              <div className="mt-4 flex justify-center gap-1.5">
                {scrollSnaps.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    aria-label={`Go to slide ${index + 1}`}
                    onClick={() => emblaApi?.scrollTo(index)}
                    className={cn(
                      'h-1.5 rounded-full transition-all duration-300',
                      index === selectedIndex
                        ? 'w-5 bg-primary-500'
                        : 'w-1.5 bg-neutral-300 hover:bg-neutral-400',
                    )}
                  />
                ))}
              </div>
            )}

          </Section>
        </AnimateIn>
      </Container>

      {modalVideo?.platform === 'youtube' && modalVideo.videoId && (
        <VideoModal video={modalVideo} onClose={() => setModalVideo(null)} />
      )}
    </div>
  )
}

function VideoThumbnail({
  video,
  isActive,
  className,
}: {
  video: ShowcaseVideo
  isActive: boolean
  className?: string
}) {
  const fallbacks = getShowcaseThumbnailFallbacks(video)
  const [srcIndex, setSrcIndex] = useState(0)
  const showYoutubePreview =
    isActive && video.platform === 'youtube' && Boolean(video.videoId)

  useEffect(() => {
    setSrcIndex(0)
  }, [video.id])

  return (
    <div className={cn('relative aspect-video overflow-hidden bg-neutral-100', className)}>
      {showYoutubePreview ? (
        <iframe
          src={getYoutubePreviewEmbedUrl(video.videoId!)}
          title={`${video.title} preview`}
          className="pointer-events-none absolute inset-0 h-full w-full scale-[1.02] object-cover"
          allow="autoplay; encrypted-media"
          tabIndex={-1}
        />
      ) : (
        <img
          src={fallbacks[srcIndex]}
          alt={video.title}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          loading="lazy"
          onError={() => {
            if (srcIndex < fallbacks.length - 1) {
              setSrcIndex((index) => index + 1)
            }
          }}
        />
      )}
      <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-black/30 via-transparent to-transparent" />

      {video.duration && (
        <span className="absolute bottom-2 right-2 rounded-md bg-black/75 px-2 py-0.5 text-[10px] font-semibold text-white">
          {video.duration}
        </span>
      )}

      {!showYoutubePreview && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-primary-600 shadow-md transition-transform duration-300 group-hover:scale-105">
            <Play className="ml-0.5 h-4 w-4 fill-current" />
          </span>
        </span>
      )}
    </div>
  )
}

function VideoCard({
  video,
  isActive,
  onOpen,
}: {
  video: ShowcaseVideo
  isActive: boolean
  onOpen: () => void
}) {
  const meta = [
    video.location,
    video.views != null ? `${formatMediaCount(video.views)} views` : null,
  ].filter(Boolean)

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Play video: ${video.title}`}
      className={cn(
        'group flex h-full w-full flex-col overflow-hidden rounded-2xl border bg-white text-left shadow-[0_2px_10px_rgba(15,23,42,0.05)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,93,154,0.12)]',
        isActive ? 'border-primary-200 ring-1 ring-primary-100' : 'border-neutral-200/90',
      )}
    >
      <VideoThumbnail video={video} isActive={isActive} />

      <div className="flex flex-col gap-1 px-3.5 py-3 sm:px-4 sm:py-3.5">
        <h3 className="font-display text-sm font-bold leading-snug text-neutral-900 line-clamp-2 transition-colors group-hover:text-primary-600 sm:text-[15px]">
          {video.title}
        </h3>
        {meta.length > 0 && (
          <p className="text-[11px] leading-relaxed text-neutral-500 line-clamp-1 sm:text-xs">
            {meta.join(' · ')}
          </p>
        )}
      </div>
    </button>
  )
}

function VideoModal({ video, onClose }: { video: ShowcaseVideo; onClose: () => void }) {
  if (!video.videoId) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[820px] overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close video"
          className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg bg-white/95 text-neutral-700 shadow-sm hover:bg-white"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="relative aspect-video bg-black">
          <iframe
            src={getYoutubeEmbedUrl(video.videoId)}
            title={video.title}
            className="absolute inset-0 h-full w-full border-0"
            allow="autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="border-t border-neutral-100 px-4 py-3">
          <p className="font-display text-sm font-bold text-neutral-900">{video.title}</p>
          {(video.builderName || video.location) && (
            <p className="mt-0.5 text-xs text-neutral-500">
              {[video.builderName, video.location].filter(Boolean).join(' · ')}
            </p>
          )}
          <a
            href={getShowcaseOpenUrl(video)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline"
          >
            Open on YouTube
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  )
}
